import { UserRowProps } from './types';
import Icon from 'components/icons';
import { AvatarLetter } from 'components/avatar';
import Checkbox from 'components/checkbox';
import { useRef, useState } from 'react';
import { Dropdown } from 'antd';
import UserRowTooltip from './UserRowTooltip';
import {
  DropdownArrow,
  LeftSide,
  Row,
  UserEmail,
  UserInfo,
  UserName,
  VoucherDropdownButton,
  VoucherDropdownMenu,
  VoucherItem,
  VoucherItemLabel,
  VoucherList,
  VoucherSelector,
  VoucherSelectorControls,
  TooltipWrapperWithMargin,
} from './styles';

const UserRow = ({
  user,
  examOptions,
  isSelected,
  selectedExamIds,
  failedExamIds,
  onCheckboxChange,
  onExamChange,
  canAssignMore,
}: UserRowProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedCount = selectedExamIds.length;
  const placeholder =
    selectedCount > 0
      ? `${selectedCount} voucher${selectedCount !== 1 ? 's' : ''} selected`
      : 'Available vouchers';

  const dropdownWidth = buttonRef.current?.offsetWidth || 220;

  const getDropdownPlacement = (): 'bottomLeft' | 'topLeft' => {
    if (!buttonRef.current) return 'bottomLeft';
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 250; // max-height from styles
    return spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottomLeft' : 'topLeft';
  };

  const voucherMenu = (
    <VoucherDropdownMenu isOpen={true} role="menu">
      <VoucherList>
        {examOptions.map((exam) => {
          const isEligible = user.eligible_certifications?.includes(exam.id) ?? true;
          const isDisabled = !isEligible || (!canAssignMore && !selectedExamIds.includes(exam.id));
          const hasFailed = failedExamIds.includes(exam.id);

          const handleItemClick = (e: React.MouseEvent) => {
            // don't trigger if clicking on tooltip icon
            if ((e.target as HTMLElement).closest('[role="img"]')) {
              e.stopPropagation();
              return;
            }
            if (isEligible && !isDisabled) {
              onExamChange(user.id, exam.id, !selectedExamIds.includes(exam.id));
            }
          };

          return (
            <VoucherItem
              key={exam.id}
              role="none"
              onClick={handleItemClick}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <VoucherItemLabel isEligible={isEligible}>
                <Checkbox
                  checked={selectedExamIds.includes(exam.id)}
                  onChange={(e) => e.stopPropagation()}
                  disabled={isDisabled}
                  controlled
                  label={exam.code}
                  aria-label={`${exam.code}${isEligible ? '' : ' (not eligible)'}${
                    hasFailed ? ' (assignment failed)' : ''
                  }`}
                />
              </VoucherItemLabel>
              {hasFailed && (
                <TooltipWrapperWithMargin>
                  <UserRowTooltip
                    message="Assignment failed for this exam"
                    ariaLabel="Assignment failed for this exam"
                  />
                </TooltipWrapperWithMargin>
              )}
              {!isEligible && (
                <TooltipWrapperWithMargin>
                  <UserRowTooltip
                    message="User already has the voucher or has earned the certification but is not in the renewal period."
                    iconFill="var(--lilac)"
                    ariaLabel="User not eligible for this exam"
                  />
                </TooltipWrapperWithMargin>
              )}
            </VoucherItem>
          );
        })}
      </VoucherList>
    </VoucherDropdownMenu>
  );

  return (
    <Row role="listitem">
      <LeftSide>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onCheckboxChange(user.id, e.target.checked)}
          disabled={!canAssignMore && !isSelected}
          controlled
          className="user-row__checkbox"
          aria-label={`Select ${user.name || user.email} to assign vouchers`}
        />
        <AvatarLetter avatar={user.avatarUrl} name={user.name || user.email} size="sm" />
        <UserInfo>
          <UserName>{user.name || user.email}</UserName>
          <UserEmail>{user.email}</UserEmail>
        </UserInfo>
      </LeftSide>
      {isSelected && (
        <VoucherSelector>
          <VoucherSelectorControls>
            <Dropdown
              overlay={voucherMenu}
              trigger={['click']}
              visible={isDropdownOpen}
              onVisibleChange={setIsDropdownOpen}
              placement={getDropdownPlacement()}
              overlayStyle={{ width: dropdownWidth }}
              align={{ offset: [0, 4] }}
            >
              <VoucherDropdownButton
                ref={buttonRef}
                type="button"
                aria-label={`Select exam vouchers for ${user.name || user.email}${
                  failedExamIds.length > 0 ? ' (assignment failed)' : ''
                }`}
                aria-expanded={isDropdownOpen}
                aria-haspopup="menu"
              >
                <span>{placeholder}</span>
                <DropdownArrow isOpen={isDropdownOpen} aria-hidden="true">
                  <Icon icon="ChevronDown" size="smaller" />
                </DropdownArrow>
              </VoucherDropdownButton>
            </Dropdown>
            {failedExamIds.length > 0 && (
              <UserRowTooltip
                message={`${failedExamIds.length} voucher assignment${
                  failedExamIds.length !== 1 ? 's' : ''
                } failed for this user`}
              />
            )}
          </VoucherSelectorControls>
        </VoucherSelector>
      )}
    </Row>
  );
};

export default UserRow;
